import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDomains, runCheck, getMonitoringConfig, saveMonitoringConfig, getMonitoringLastRun } from '../lib/api';
import { Shield, CheckCircle, AlertCircle, TrendingUp, Activity, Globe, Mail, Key, FileText, RefreshCw, Plus, Settings, Lock, Server, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

// Map API check keys to state keys
const CHECK_MAP = {
  'ssl': 'ssl', 'email-auth': 'emailAuth', 'reputation': 'reputation',
  'uptime': 'uptime', 'breach': 'breach', 'ct': 'ct', 'cdn': 'cdn'
};
const CHECK_KEYS = Object.keys(CHECK_MAP);

// Which checks need additional configuration before they can run
const SETUP_REQUIRED = {
  uptime: {
    configKey: 'uptime',
    label: 'Configure Endpoints',
    description: 'Uptime monitoring requires you to specify the endpoints (URLs) to monitor. We do not assume endpoints from the domain name alone.',
    hint: 'Add the URLs you want to monitor for availability, e.g., your homepage, API health endpoint, login page, etc.',
    isConfigured: (config) => config?.uptime?.endpoints?.length > 0,
  },
};

const AUTO_CHECKS = ['ssl', 'email-auth', 'reputation', 'ct', 'cdn', 'breach'];

// Monitoring plan tiers
const MONITORING_TIERS = {
  free: { name: 'Free', price: null, domains: 1, description: '1 domain' },
  standard: { name: 'Standard', price: 199, pricePeriod: '/month', domains: 5, description: 'Up to 5 domains' },
};

function formatLastRun(ts) {
  if (!ts) return null;
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

// ── Shared inline style helpers (matching balanced.css patterns) ──

const S = {
  label: { fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray)' },
  sectionTitle: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--charcoal)', letterSpacing: '-0.3px' },
  card: { background: 'var(--white)', border: '1px solid var(--gray)', borderRadius: '0.75rem', padding: '2rem', transition: 'all 0.2s ease' },
  cardEmphasis: { background: 'var(--white)', border: '2px solid var(--charcoal)', borderRadius: '0.75rem', padding: '2rem' },
  metricTile: { background: 'var(--light-gray)', border: '1px solid var(--gray)', borderRadius: '0.5rem', padding: '1.25rem' },
  mono: { fontFamily: "'IBM Plex Mono', monospace" },
};

const MonitoringDashboard = () => {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [monitoringData, setMonitoringData] = useState(null);
  const [monitoringConfig, setMonitoringConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [checksLoading, setChecksLoading] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [configuring, setConfiguring] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [uptimeEndpoints, setUptimeEndpoints] = useState([{ label: '', path: '/', headers: [] }]);
  const [lastRun, setLastRun] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    fetchData();
    return () => { mounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      if (!user) { if (mounted.current) { setDomains([]); setLoading(false); } return; }
      const { domains: domainsData } = await getDomains();
      if (!mounted.current) return;
      setDomains(domainsData || []);
      if (mounted.current) setLoading(false);
      if (domainsData && domainsData.length > 0) {
        setSelectedDomain(domainsData[0]);
        initDomain(domainsData[0].domain_name);
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
      if (mounted.current) setLoading(false);
    }
  };

  const initDomain = async (domainName) => {
    let config = {};
    try {
      const res = await getMonitoringConfig(domainName);
      config = res.config || {};
    } catch (e) { console.error('Failed to load monitoring config:', e); }
    if (mounted.current) {
      setMonitoringConfig(config);
      if (config?.uptime?.endpoints?.length > 0) {
        setUptimeEndpoints(config.uptime.endpoints.map(ep => ({
          label: ep.label || ep.name || '',
          path: ep.path || '/',
          headers: ep.headers || []
        })));
      } else {
        setUptimeEndpoints([{ label: '', path: '/', headers: [] }]);
      }
    }
    fetchMonitoringData(domainName, config);
    getMonitoringLastRun(domainName).then((res) => {
      if (mounted.current && res?.lastRun) setLastRun(res.lastRun);
    }).catch(() => {});
  };

  const fetchMonitoringData = (domain, config) => {
    const cfg = config || monitoringConfig;
    setMonitoringData(prev => prev || {
      ssl: null, emailAuth: null, reputation: null,
      uptime: null, breach: null, ct: null, cdn: null, overallScore: 0
    });
    setChecksLoading(CHECK_KEYS.length);

    CHECK_KEYS.forEach(checkKey => {
      const stateKey = CHECK_MAP[checkKey];
      const setupInfo = SETUP_REQUIRED[stateKey];
      if (setupInfo && !setupInfo.isConfigured(cfg)) {
        if (mounted.current) {
          setMonitoringData(prev => ({ ...prev, [stateKey]: { needsSetup: true, checkType: stateKey, message: setupInfo.description } }));
          setChecksLoading(prev => prev - 1);
        }
        return;
      }
      runCheck(checkKey, domain)
        .then(result => {
          if (!mounted.current) return;
          setMonitoringData(prev => {
            const updated = { ...prev, [stateKey]: result };
            const scores = Object.values(CHECK_MAP).map(k => updated[k]).filter(d => d && !d.needsSetup && d.score != null).map(d => d.score);
            updated.overallScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
            return updated;
          });
        })
        .catch(err => console.error(`Check ${checkKey} failed:`, err))
        .finally(() => { if (mounted.current) setChecksLoading(prev => prev - 1); });
    });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    if (selectedDomain) {
      initDomain(selectedDomain.domain_name);
      setTimeout(() => setRefreshing(false), 500);
      setTimeout(() => {
        getMonitoringLastRun(selectedDomain.domain_name).then((res) => {
          if (res?.lastRun) setLastRun(res.lastRun);
        }).catch(() => {});
      }, 3000);
    } else {
      setRefreshing(false);
    }
  };

  const handleSaveUptimeConfig = async () => {
    const valid = uptimeEndpoints.filter(ep => ep.path.trim());
    if (valid.length === 0) return;
    setSavingConfig(true);
    try {
      const domain = selectedDomain.domain_name;
      const endpoints = valid.map(ep => {
        const cleanPath = ep.path.trim().startsWith('/') ? ep.path.trim() : `/${ep.path.trim()}`;
        return {
          label: ep.label.trim() || cleanPath,
          path: cleanPath,
          headers: (ep.headers || []).filter(h => h.key.trim()),
          expectedStatus: [200, 201, 202, 204, 301, 302]
        };
      });
      const res = await saveMonitoringConfig(domain, { uptime: { endpoints } });
      if (mounted.current) {
        setMonitoringConfig(res.config);
        setConfiguring(null);
        setMonitoringData(prev => ({ ...prev, uptime: null }));
        runCheck('uptime', domain).then(result => {
          if (mounted.current) {
            setMonitoringData(prev => {
              const updated = { ...prev, uptime: result };
              const scores = Object.values(CHECK_MAP).map(k => updated[k]).filter(d => d && !d.needsSetup && d.score != null).map(d => d.score);
              updated.overallScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
              return updated;
            });
          }
        }).catch(err => console.error('Uptime check failed after config:', err));
      }
    } catch (e) { console.error('Failed to save config:', e); }
    finally { setSavingConfig(false); }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'var(--success)';
    if (score >= 70) return 'var(--gold)';
    return 'var(--error)';
  };

  const getScoreGrade = (score) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const monitoringCategories = [
    { key: 'ssl', name: 'SSL / TLS', icon: Lock, description: 'Certificate validity, expiration, and TLS configuration' },
    { key: 'emailAuth', name: 'Email Auth', icon: Mail, description: 'SPF, DKIM, and DMARC record validation' },
    { key: 'reputation', name: 'Reputation', icon: Globe, description: 'DNS blacklist and phishing database checks' },
    { key: 'breach', name: 'Breach Exposure', icon: Key, description: 'Credential leak and data breach monitoring' },
    { key: 'ct', name: 'Cert Transparency', icon: FileText, description: 'Certificate issuance tracking via CT logs' },
    { key: 'uptime', name: 'Service Uptime', icon: Activity, description: 'Endpoint availability, DNS, and response time' },
    { key: 'cdn', name: 'CDN Config', icon: Server, description: 'CDN detection, origin exposure, and security headers' }
  ];

  const getCardDetails = (key, data) => {
    if (!data) return null;
    switch (key) {
      case 'ssl': { const cert = data.certificate; if (!cert) return null; const items = []; if (cert.issuer && cert.issuer !== 'Unknown') items.push(`Issuer: ${cert.issuer}`); if (cert.daysUntilExpiry != null) items.push(`Expires in ${cert.daysUntilExpiry} days`); const tls = data.configuration?.tlsVersion; if (tls && tls !== 'Unknown') items.push(`TLS: ${tls}`); return items; }
      case 'emailAuth': return [`SPF: ${data.spf?.exists ? (data.spf.valid ? 'Valid' : 'Invalid') : 'Missing'}`, `DKIM: ${data.dkim?.exists ? 'Found' : 'Not found'}`, `DMARC: ${data.dmarc?.exists ? `Policy: ${data.dmarc.policy || 'set'}` : 'Missing'}`];
      case 'reputation': { const bl = data.blacklists; return bl ? [`${bl.checked} blacklists checked, ${bl.listed} listed`, data.phishing?.reported ? `Phishing reports: ${data.phishing.reports}` : 'No phishing reports'] : null; }
      case 'breach': return [`${data.totalBreaches || 0} known breach${(data.totalBreaches || 0) !== 1 ? 'es' : ''}`, data.exposedCredentials?.count > 0 ? `${data.exposedCredentials.count} exposed credentials` : 'No exposed credentials'];
      case 'ct': { const items = [`${data.totalCertificates || 0} certs in CT logs, ${data.recentCertificates?.length || 0} recent`]; if (data.issuers?.length > 0) items.push(`${data.issuers.length} CA${data.issuers.length !== 1 ? 's' : ''}: ${data.issuers.slice(0, 2).map(i => i.name).join(', ')}${data.issuers.length > 2 ? '...' : ''}`); if (data.expiringSoon > 0) items.push(`${data.expiringSoon} expiring soon`); return items; }
      case 'uptime': { if (data.needsSetup) return null; const items = []; if (data.totalEndpoints) items.push(`${data.endpointsUp}/${data.totalEndpoints} endpoints UP`); else items.push(data.up ? 'Site is UP' : 'Site is DOWN'); if (data.responseTime) items.push(`Avg response: ${data.responseTime}ms`); return items; }
      case 'cdn': return [data.cdn?.detected ? `CDN: ${data.cdn.provider || 'Detected'}` : 'No CDN detected', data.origin?.exposed ? 'Origin IP exposed' : 'Origin protected'];
      default: return null;
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <RefreshCw size={48} style={{ animation: 'spin 1s linear infinite', color: 'var(--gray)' }} />
      </div>
    );
  }

  // ── Empty state ──
  if (domains.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--white)', padding: '6rem 2rem' }}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <Shield size={64} style={{ color: 'var(--gold)', marginBottom: '2rem' }} />
          <h1 className="section-title" style={{ marginBottom: '1rem' }}>Start Monitoring</h1>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem', color: 'var(--gray)', lineHeight: 1.6 }}>
            Add your first domain to start monitoring SSL, email authentication, reputation, and more.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2.5rem', fontSize: '0.875rem' }}>
            <span style={{ padding: '0.4rem 0.75rem', background: 'var(--light-gray)', borderRadius: '0.5rem', color: 'var(--charcoal)' }}><strong>Free</strong> — 1 domain</span>
            <span style={{ padding: '0.4rem 0.75rem', border: '2px solid var(--gold)', borderRadius: '0.5rem', color: 'var(--charcoal)' }}><strong>Standard</strong> — $199/month, up to 5 domains</span>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard/monitoring/onboarding')}>
            Add Domain
          </button>
        </div>
      </div>
    );
  }

  // ── Dashboard ──
  return (
    <div style={{ minHeight: '100vh', background: 'var(--white)' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .dash-card { transition: all 0.2s ease; cursor: pointer; }
        .dash-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); border-color: var(--charcoal) !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)', padding: '1.25rem 2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--black)', letterSpacing: '-0.5px', marginBottom: '0.1rem' }}>Trust Monitoring</h1>
            <p style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>Real-time security posture for your domains</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select
              value={selectedDomain?.id || ''}
              onChange={(e) => {
                const domain = domains.find(d => d.id === e.target.value);
                setSelectedDomain(domain);
                setMonitoringData(null);
                initDomain(domain.domain_name);
              }}
              className="form-input"
              style={{ padding: '0.5rem 1rem', fontWeight: 600, minWidth: '200px' }}
            >
              {domains.map(d => <option key={d.id} value={d.id}>{d.domain_name}</option>)}
            </select>
            <button onClick={handleRefresh} disabled={refreshing} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> Refresh
            </button>
            <button onClick={() => {
              // Free tier: 1 domain max — redirect to plan chooser with limit flag
              if (domains.length >= MONITORING_TIERS.free.domains) {
                navigate('/dashboard/monitoring/onboarding?fromLimit=true');
              } else {
                navigate('/dashboard/monitoring/onboarding');
              }
            }} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus size={14} /> Add Domain
            </button>
          </div>
        </div>

        {/* ── Plan tiers ── */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--light-gray)' }}>
          <div style={{ ...S.label, marginBottom: '0.5rem' }}>Plans</div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'var(--light-gray)', borderRadius: '0.5rem', border: '1px solid var(--gray)' }}>
              <span style={{ fontWeight: 700, color: 'var(--charcoal)' }}>Free</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>1 domain</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'var(--white)', borderRadius: '0.5rem', border: '2px solid var(--gold)', boxShadow: '0 0 0 1px var(--gold)' }}>
              <span style={{ fontWeight: 700, color: 'var(--charcoal)' }}>Standard</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gold)' }}>$199/month</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>· up to 5 domains</span>
            </div>
            <span style={{ fontSize: '0.8rem', color: 'var(--gray)', marginLeft: '0.25rem' }}>
              You're monitoring <strong style={{ color: 'var(--charcoal)' }}>{domains.length}</strong> {domains.length === 1 ? 'domain' : 'domains'}
              {domains.length > MONITORING_TIERS.free.domains && (
                <span style={{ marginLeft: '0.5rem' }}>· <Link to="/pricing" style={{ color: 'var(--gold)', fontWeight: 600, textDecoration: 'none' }}>Upgrade to Standard</Link></span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* ── Score + Domain Banner ── */}
      {monitoringData && (
        <div style={{ background: 'var(--charcoal)', padding: '2.5rem 2rem', color: 'var(--white)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '3rem', flexWrap: 'wrap' }}>
            {/* Score Ring */}
            <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
              <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
                <circle cx="70" cy="70" r="60" fill="none" stroke="var(--gold)" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(monitoringData.overallScore / 100) * 377} 377`} style={{ transition: 'all 0.6s ease' }} />
              </svg>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--gold)', lineHeight: 1 }}>{monitoringData.overallScore}</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginTop: '0.1rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{getScoreGrade(monitoringData.overallScore)}-Grade</div>
              </div>
            </div>

            {/* Quick stats */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
              {monitoringCategories.slice(0, 5).map(cat => {
                const Icon = cat.icon;
                const checkData = monitoringData[cat.key];
                const isNeedsSetup = checkData?.needsSetup;
                const score = isNeedsSetup ? null : checkData?.score;
                const isLoading = !checkData;
                return (
                  <div key={cat.key} onClick={() => setActiveTab(cat.key)}
                    style={{ background: activeTab === cat.key ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.06)', border: activeTab === cat.key ? '1px solid var(--gold)' : '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', padding: '0.75rem', cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.3rem' }}>
                      <Icon size={12} style={{ color: 'var(--gold)', opacity: 0.8 }} />
                      <span style={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{cat.name}</span>
                    </div>
                    {isNeedsSetup ? (
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold)' }}>Setup</div>
                    ) : isLoading ? (
                      <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', color: 'rgba(255,255,255,0.4)' }} />
                    ) : (
                      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--white)' }}>{score}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Domain info */}
            <div style={{ minWidth: '200px' }}>
              <div style={{ ...S.label, color: 'rgba(255,255,255,0.5)', marginBottom: '0.35rem', fontSize: '0.65rem' }}>Monitored Domain</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--white)', marginBottom: '0.5rem', ...S.mono }}>{selectedDomain?.domain_name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {selectedDomain?.verification_status === 'VERIFIED' ? (
                  <><CheckCircle size={14} style={{ color: 'var(--success)' }} /><span style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.8rem' }}>Verified</span></>
                ) : (
                  <><AlertCircle size={14} style={{ color: 'var(--gold)' }} /><span style={{ color: 'var(--gold)', fontWeight: 600, fontSize: '0.8rem' }}>Pending</span></>
                )}
              </div>
            </div>
            {Object.keys(lastRun).length > 0 && (
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem', gridColumn: '1 / -1' }}>
                <span style={{ fontWeight: 600 }}>Last automated run:</span>{' '}
                {['ssl', 'uptime', 'email-auth', 'reputation', 'breach', 'ct', 'cdn'].map(k => lastRun[k] ? `${k.replace('-', ' ')} ${formatLastRun(lastRun[k])}` : null).filter(Boolean).join(' • ') || '—'}
                <span style={{ marginLeft: '0.75rem', opacity: 0.8 }}>Uptime every 15 min · SSL/email hourly · Reputation, CT, CDN every 6h</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab Nav ── */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '0', overflowX: 'auto', padding: '0 2rem' }}>
          {[{ key: 'overview', name: 'Overview' }, ...monitoringCategories].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ background: 'transparent', border: 'none', borderBottom: activeTab === tab.key ? '3px solid var(--gold)' : '3px solid transparent', color: activeTab === tab.key ? 'var(--black)' : 'var(--gray)', padding: '0.85rem 1.25rem', fontWeight: activeTab === tab.key ? 700 : 500, cursor: 'pointer', fontSize: '0.875rem', whiteSpace: 'nowrap', transition: 'color 0.2s' }}>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2.5rem 2rem' }}>

        {/* ── Overview Grid ── */}
        {activeTab === 'overview' && monitoringData && (
          <div>
            <div style={{ ...S.label, marginBottom: '1.5rem' }}>Security Checks</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
              {monitoringCategories.map(cat => {
                const Icon = cat.icon;
                const data = monitoringData[cat.key];
                const isNeedsSetup = data?.needsSetup;
                const score = isNeedsSetup ? 0 : (data?.score || 0);
                const isLoading = !data;
                return (
                  <div key={cat.key} className="dash-card"
                    onClick={() => data ? setActiveTab(cat.key) : null}
                    style={{ ...S.card, padding: '1.5rem', border: isNeedsSetup ? '2px dashed var(--gold)' : '1px solid var(--gray)', opacity: isLoading ? 0.6 : 1 }}>

                    {/* Header row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '42px', height: '42px', background: 'var(--light-gray)', border: '1px solid var(--gray)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon size={20} style={{ color: isNeedsSetup ? 'var(--gold)' : 'var(--charcoal)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--black)', fontSize: '0.95rem' }}>{cat.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>{cat.description}</div>
                        </div>
                      </div>
                      {isLoading ? (
                        <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--gray)' }} />
                      ) : isNeedsSetup ? (
                        <div style={{ background: 'var(--gold)', color: 'var(--black)', fontSize: '0.65rem', fontWeight: 700, padding: '0.25rem 0.6rem', borderRadius: '9999px', textTransform: 'uppercase' }}>Setup</div>
                      ) : (
                        <div style={{ minWidth: '48px', textAlign: 'right' }}>
                          <div style={{ fontSize: '1.75rem', fontWeight: 900, color: getScoreColor(score), lineHeight: 1 }}>{score}</div>
                          <div style={{ fontSize: '0.6rem', fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Score</div>
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    {!isNeedsSetup && (
                      <div style={{ height: '4px', background: 'var(--light-gray)', borderRadius: '2px', overflow: 'hidden', marginBottom: '1rem' }}>
                        <div style={{ height: '100%', width: isLoading ? '0%' : `${score}%`, background: isLoading ? 'var(--gray)' : getScoreColor(score), borderRadius: '2px', transition: 'width 0.6s ease' }} />
                      </div>
                    )}

                    {/* Details */}
                    {!isLoading && !isNeedsSetup && (() => {
                      const details = getCardDetails(cat.key, data);
                      return details && details.length > 0 ? (
                        <div style={{ marginBottom: '1rem' }}>
                          {details.map((item, i) => (
                            <div key={i} style={{ fontSize: '0.8rem', color: 'var(--charcoal)', marginBottom: '0.15rem' }}>
                              <span style={{ color: 'var(--gold)', marginRight: '0.4rem' }}>—</span>{item}
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}

                    {/* Status footer */}
                    {isLoading ? (
                      <div style={{ ...S.label, fontSize: '0.8rem', color: 'var(--gray)' }}>Scanning...</div>
                    ) : isNeedsSetup ? (
                      <div style={{ background: 'var(--light-gray)', border: '1px solid var(--gray)', padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--charcoal)', marginBottom: '0.25rem' }}>
                          {SETUP_REQUIRED[cat.key]?.hint || data.message}
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold)' }}>Click to configure →</div>
                      </div>
                    ) : data?.alerts?.length > 0 ? (
                      <div style={{ background: 'var(--light-gray)', border: '1px solid var(--error)', padding: '0.6rem 0.75rem', borderRadius: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 700, fontSize: '0.8rem', color: 'var(--error)' }}>
                          <AlertCircle size={14} /> {data.alerts.length} issue{data.alerts.length > 1 ? 's' : ''} found
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, fontSize: '0.8rem', color: 'var(--success)' }}>
                        <CheckCircle size={14} /> All checks passing
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Individual Tab Content ── */}
        {activeTab !== 'overview' && monitoringData && (() => {
          const cat = monitoringCategories.find(c => c.key === activeTab);
          const data = monitoringData[activeTab];
          if (!cat) return null;

          // ── Setup Required ──
          if (data?.needsSetup) {
            const setup = SETUP_REQUIRED[cat.key];
            return (
              <div style={{ ...S.cardEmphasis, borderColor: 'var(--gold)', borderStyle: 'dashed' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                  <cat.icon size={28} style={{ color: 'var(--gold)' }} />
                  <div>
                    <h2 style={{ ...S.sectionTitle }}>{cat.name}</h2>
                    <span style={{ background: 'var(--gold)', color: 'var(--black)', fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '9999px', textTransform: 'uppercase' }}>Setup Required</span>
                  </div>
                </div>

                <div style={{ ...S.metricTile, marginBottom: '2rem' }}>
                  <p style={{ fontWeight: 600, color: 'var(--charcoal)', marginBottom: '0.5rem' }}>{setup?.label || 'Configuration Needed'}</p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.25rem' }}>{setup?.description || data.message}</p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>{setup?.hint || ''}</p>
                </div>

                {cat.key === 'uptime' && (
                  <div>
                    <h3 className="subsection-title" style={{ marginBottom: '1rem' }}>Add Endpoints to Monitor</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>
                      Add the paths you want to check for uptime. All paths are relative to <strong style={{ ...S.mono, color: 'var(--charcoal)' }}>https://{selectedDomain?.domain_name}</strong>.
                    </p>
                    <p style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '1.25rem' }}>
                      Examples: <code style={S.mono}>/</code> (homepage), <code style={S.mono}>/api/health</code>, <code style={S.mono}>/login</code>
                    </p>
                    {uptimeEndpoints.map((ep, i) => (
                      <div key={i} style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--light-gray)', borderRadius: '0.5rem', border: '1px solid var(--gray)' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                          <input type="text" placeholder="Label (e.g. Homepage)" value={ep.label}
                            onChange={(e) => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], label: e.target.value }; setUptimeEndpoints(u); }}
                            className="form-input" style={{ flex: 1 }} />
                          <div style={{ display: 'flex', alignItems: 'center', flex: 2, gap: 0 }}>
                            <span style={{ ...S.mono, fontSize: '0.8rem', color: 'var(--gray)', padding: '0.5rem 0.5rem', background: 'var(--white)', border: '1px solid var(--gray)', borderRight: 'none', borderRadius: '0.375rem 0 0 0.375rem', whiteSpace: 'nowrap' }}>https://{selectedDomain?.domain_name}</span>
                            <input type="text" placeholder="/api/health" value={ep.path}
                              onChange={(e) => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], path: e.target.value }; setUptimeEndpoints(u); }}
                              className="form-input" style={{ flex: 1, borderRadius: '0 0.375rem 0.375rem 0' }} />
                          </div>
                          {uptimeEndpoints.length > 1 && (
                            <button onClick={() => setUptimeEndpoints(uptimeEndpoints.filter((_, j) => j !== i))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--error)' }}><Trash2 size={16} /></button>
                          )}
                        </div>
                        {/* Custom Headers (collapsible) */}
                        <details style={{ marginTop: '0.5rem' }}>
                          <summary style={{ fontSize: '0.75rem', color: 'var(--gray)', cursor: 'pointer', userSelect: 'none' }}>Custom Headers {ep.headers?.length > 0 ? `(${ep.headers.length})` : ''}</summary>
                          <div style={{ marginTop: '0.5rem' }}>
                            {(ep.headers || []).map((h, hi) => (
                              <div key={hi} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem', alignItems: 'center' }}>
                                <input type="text" placeholder="Header name" value={h.key}
                                  onChange={(e) => { const u = [...uptimeEndpoints]; const hd = [...(u[i].headers || [])]; hd[hi] = { ...hd[hi], key: e.target.value }; u[i] = { ...u[i], headers: hd }; setUptimeEndpoints(u); }}
                                  className="form-input" style={{ flex: 1, fontSize: '0.8rem', padding: '0.35rem 0.5rem' }} />
                                <input type="text" placeholder="Value" value={h.value}
                                  onChange={(e) => { const u = [...uptimeEndpoints]; const hd = [...(u[i].headers || [])]; hd[hi] = { ...hd[hi], value: e.target.value }; u[i] = { ...u[i], headers: hd }; setUptimeEndpoints(u); }}
                                  className="form-input" style={{ flex: 2, fontSize: '0.8rem', padding: '0.35rem 0.5rem' }} />
                                <button onClick={() => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], headers: (u[i].headers || []).filter((_, j) => j !== hi) }; setUptimeEndpoints(u); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--error)', fontSize: '0.9rem' }}>×</button>
                              </div>
                            ))}
                            <button onClick={() => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], headers: [...(u[i].headers || []), { key: '', value: '' }] }; setUptimeEndpoints(u); }}
                              style={{ fontSize: '0.75rem', color: 'var(--charcoal)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: '0.25rem 0' }}>+ Add Header</button>
                          </div>
                        </details>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                      <button onClick={() => setUptimeEndpoints([...uptimeEndpoints, { label: '', path: '/', headers: [] }])} className="btn btn-secondary">+ Add Endpoint</button>
                      <button onClick={handleSaveUptimeConfig} disabled={savingConfig || uptimeEndpoints.filter(ep => ep.path.trim()).length === 0} className="btn btn-primary">
                        {savingConfig ? 'Saving...' : 'Save & Start Monitoring'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          if (!data) return null;

          // ── Detail Panel ──
          return (
            <div style={{ ...S.cardEmphasis }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--gray)' }}>
                <cat.icon size={28} style={{ color: 'var(--charcoal)' }} />
                <div style={{ flex: 1 }}>
                  <h2 style={{ ...S.sectionTitle }}>{cat.name}</h2>
                  <p style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>{cat.description}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 900, color: getScoreColor(data.score), lineHeight: 1 }}>{data.score}</div>
                  <div style={{ ...S.label, fontSize: '0.65rem', marginTop: '0.15rem' }}>Score</div>
                </div>
              </div>

              {/* Status row */}
              <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ ...S.label, marginBottom: '0.5rem', fontSize: '0.7rem' }}>Status</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {data.status === 'healthy' || data.status === 'clean' ? (
                      <><CheckCircle size={16} style={{ color: 'var(--success)' }} /><span style={{ color: 'var(--success)', fontWeight: 600 }}>Healthy</span></>
                    ) : data.status === 'warning' ? (
                      <><AlertCircle size={16} style={{ color: 'var(--gold)' }} /><span style={{ color: 'var(--gold)', fontWeight: 600 }}>Warning</span></>
                    ) : (
                      <><AlertCircle size={16} style={{ color: 'var(--error)' }} /><span style={{ color: 'var(--error)', fontWeight: 600 }}>Critical</span></>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ ...S.label, marginBottom: '0.5rem', fontSize: '0.7rem' }}>Last Checked</div>
                  <div style={{ color: 'var(--charcoal)', fontSize: '0.9rem' }}>{data.timestamp ? new Date(data.timestamp).toLocaleString() : 'N/A'}</div>
                </div>
              </div>

              {/* ── SSL ── */}
              {activeTab === 'ssl' && (
                <div>
                  {/* Top metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>TLS Version</div>
                      <div style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--charcoal)' }}>{data.configuration?.tlsVersion || 'Unknown'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>Protocol version</div>
                    </div>
                    <div style={{ ...S.metricTile, borderColor: data.certificate?.daysUntilExpiry != null && data.certificate.daysUntilExpiry <= 30 ? 'var(--gold)' : 'var(--gray)' }}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Days Until Expiry</div>
                      <div style={{ fontWeight: 700, fontSize: '1.25rem', color: data.certificate?.daysUntilExpiry != null ? (data.certificate.daysUntilExpiry <= 7 ? 'var(--error)' : data.certificate.daysUntilExpiry <= 30 ? 'var(--gold)' : 'var(--success)') : 'var(--gray)' }}>
                        {data.certificate?.daysUntilExpiry != null ? data.certificate.daysUntilExpiry : 'N/A'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>Before renewal needed</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Chain Status</div>
                      <div style={{ fontWeight: 700, fontSize: '1.25rem', color: data.chain?.valid ? 'var(--success)' : 'var(--error)' }}>{data.chain?.valid ? 'Valid' : 'Invalid'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>Trust chain verification</div>
                    </div>
                    {data.server?.http3 && (
                      <div style={S.metricTile}>
                        <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>HTTP/3</div>
                        <div style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--success)' }}>Supported</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>QUIC protocol</div>
                      </div>
                    )}
                  </div>

                  {/* Certificate Chain / Who Initiated */}
                  <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                    <div style={{ ...S.label, marginBottom: '0.75rem' }}>Certificate Chain</div>
                    {/* Initiated By */}
                    {data.chain?.initiatedBy && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--white)', borderRadius: '0.5rem', border: '1px solid var(--gray)', marginBottom: '0.5rem' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--charcoal)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: 'var(--white)', fontSize: '0.7rem', fontWeight: 700 }}>SSL</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: 'var(--black)', fontSize: '0.9rem' }}>Initiated by: {data.chain.initiatedBy}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--gray)' }}>{data.chain.certType}</div>
                        </div>
                        {data.chain.proxy && (
                          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem', background: 'var(--charcoal)', color: 'var(--white)', borderRadius: '9999px', textTransform: 'uppercase' }}>
                            {data.chain.proxy}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Visual chain: Root CA → Intermediate → Leaf */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingLeft: '1rem', borderLeft: '2px solid var(--charcoal)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--charcoal)', marginLeft: '-1.3rem', flexShrink: 0 }} />
                        <div>
                          <span style={{ fontSize: '0.7rem', color: 'var(--gray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Certificate Authority (Issuer)</span>
                          <div style={{ fontWeight: 600, color: 'var(--black)', fontSize: '0.85rem' }}>{data.certificate?.issuer || 'Unknown'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--gold)', marginLeft: '-1.3rem', flexShrink: 0 }} />
                        <div>
                          <span style={{ fontSize: '0.7rem', color: 'var(--gray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject (Your Domain)</span>
                          <div style={{ fontWeight: 600, color: 'var(--black)', fontSize: '0.85rem', ...S.mono }}>{data.certificate?.subject || data.domain}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                    <div style={{ ...S.label, marginBottom: '0.75rem' }}>Certificate Details</div>
                    {[
                      { label: 'Subject', value: data.certificate?.subject },
                      { label: 'Issuer (CA)', value: data.certificate?.issuer },
                      { label: 'Valid From', value: data.certificate?.validFrom ? new Date(data.certificate.validFrom).toLocaleDateString() : 'N/A' },
                      { label: 'Valid To', value: data.certificate?.validTo ? new Date(data.certificate.validTo).toLocaleDateString() : 'N/A' },
                      { label: 'Serial Number', value: data.certificate?.serialNumber },
                      { label: 'Signature Algorithm', value: data.certificate?.signatureAlgorithm },
                      { label: 'Source', value: data.certificate?.source === 'cache' ? 'Cached (CT logs)' : data.certificate?.source === 'crt.sh' ? 'crt.sh (CT logs)' : data.certificate?.source === 'https-fallback' ? 'HTTPS connection (details pending)' : 'N/A' },
                    ].filter(item => item.value && item.value !== 'N/A' && item.value !== 'Unknown').map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid var(--light-gray)' }}>
                        <span style={{ fontSize: '0.82rem', color: 'var(--gray)' }}>{item.label}</span>
                        <span style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--charcoal)', ...S.mono, maxWidth: '60%', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.value}>{item.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Server / Connection Info */}
                  {data.server && (
                    <div style={{ ...S.metricTile }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Connection Info</div>
                      {[
                        { label: 'Server', value: data.server.server },
                        { label: 'Proxy / CDN', value: data.server.proxy || 'None (direct)' },
                        { label: 'HSTS', value: data.server.hsts ? `Enabled${data.server.hstsValue ? ` (${data.server.hstsValue.substring(0, 50)})` : ''}` : 'Not enabled', color: data.server.hsts ? 'var(--success)' : 'var(--gold)' },
                        { label: 'HTTP/3 (QUIC)', value: data.server.http3 ? 'Supported' : 'Not advertised', color: data.server.http3 ? 'var(--success)' : 'var(--gray)' },
                        { label: 'TLS Cipher', value: data.configuration?.cipher || 'N/A' },
                        { label: 'CF-Ray', value: data.server.cfRay },
                      ].filter(item => item.value && item.value !== 'N/A').map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid var(--light-gray)' }}>
                          <span style={{ fontSize: '0.82rem', color: 'var(--gray)' }}>{item.label}</span>
                          <span style={{ fontSize: '0.82rem', fontWeight: 500, color: item.color || 'var(--charcoal)', ...S.mono }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Email Auth ── */}
              {activeTab === 'emailAuth' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                  {[
                    { title: 'SPF Record', exists: data.spf?.exists, desc: 'Specifies which servers can send email for your domain.', record: data.spf?.record, extra: data.spf?.allMechanism ? `Policy: ${data.spf.allMechanism}` : null },
                    { title: 'DKIM Record', exists: data.dkim?.exists, desc: 'Cryptographically signs outgoing emails to prevent spoofing.', extra: data.dkim?.selectors?.length ? `Selectors: ${data.dkim.selectors.join(', ')}` : null },
                    { title: 'DMARC Policy', exists: data.dmarc?.exists, desc: 'Tells receivers what to do when SPF/DKIM fail.', record: data.dmarc?.record, extra: data.dmarc?.policy ? `Policy: ${data.dmarc.policy}` : null },
                  ].map((item, i) => (
                    <div key={i} style={S.metricTile}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        {item.exists ? <CheckCircle size={16} style={{ color: 'var(--success)' }} /> : <AlertCircle size={16} style={{ color: 'var(--error)' }} />}
                        <span style={{ fontWeight: 700, color: 'var(--black)' }}>{item.title}</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '0.35rem' }}>{item.desc}</div>
                      {item.extra && <div style={{ fontSize: '0.8rem', color: 'var(--charcoal)', fontWeight: 600 }}>{item.extra}</div>}
                      {item.record && <div style={{ fontSize: '0.72rem', ...S.mono, background: 'var(--white)', padding: '0.5rem', borderRadius: '0.25rem', wordBreak: 'break-all', border: '1px solid var(--gray)', marginTop: '0.35rem' }}>{item.record}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Reputation ── */}
              {activeTab === 'reputation' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    {[
                      { label: 'Blacklists Checked', value: data.blacklists?.checked || 0, desc: 'DNS-based blacklists queried' },
                      { label: 'Listed On', value: data.blacklists?.listed || 0, desc: (data.blacklists?.listed || 0) === 0 ? 'Clean — not on any blacklist' : 'Blacklists with your domain', alert: (data.blacklists?.listed || 0) > 0 },
                      { label: 'Phishing Reports', value: data.phishing?.reports || 0, desc: data.phishing?.reported ? 'Flagged in phishing databases' : 'No phishing reports', alert: data.phishing?.reported },
                    ].map((item, i) => (
                      <div key={i} style={{ ...S.metricTile, borderColor: item.alert ? 'var(--error)' : 'var(--gray)' }}>
                        <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>{item.label}</div>
                        <div style={{ fontWeight: 700, fontSize: '1.5rem', color: item.alert ? 'var(--error)' : 'var(--charcoal)' }}>{item.value}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>{item.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Uptime ── */}
              {activeTab === 'uptime' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ ...S.metricTile, borderColor: data.up ? 'var(--success)' : 'var(--error)' }}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Overall Status</div>
                      <div style={{ fontWeight: 800, fontSize: '1.5rem', color: data.up ? 'var(--success)' : 'var(--error)' }}>{data.up ? 'ALL UP' : 'DEGRADED'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>{data.totalEndpoints ? `${data.endpointsUp}/${data.totalEndpoints} endpoints` : 'Reachability'}</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Avg Response</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--charcoal)' }}>{data.responseTime ? `${data.responseTime}ms` : 'N/A'}</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Endpoints</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--charcoal)' }}>{data.totalEndpoints || 1}</div>
                    </div>
                  </div>
                  {data.endpoints?.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Endpoint Status</div>
                      {data.endpoints.map((ep, i) => (
                        <div key={i} style={{ padding: '0.75rem 1rem', border: `1px solid ${ep.up ? 'var(--success)' : 'var(--error)'}`, borderRadius: '0.5rem', marginBottom: '0.5rem', background: 'var(--light-gray)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: ep.up ? 'var(--success)' : 'var(--error)', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: 'var(--black)', fontSize: '0.9rem' }}>{ep.endpointName}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray)', ...S.mono }}>{ep.endpointPath || ep.url}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: ep.up ? 'var(--success)' : 'var(--error)', fontSize: '0.85rem' }}>{ep.up ? 'UP' : 'DOWN'}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>
                                {ep.responseTime ? `${ep.responseTime}ms` : ''}
                                {ep.statusCode ? `${ep.responseTime ? ' · ' : ''}HTTP ${ep.statusCode}` : ''}
                              </div>
                            </div>
                          </div>
                          {/* Diagnostics for failed endpoints */}
                          {!ep.up && (ep.alerts?.length > 0 || ep.checks) && (
                            <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--gray)' }}>
                              {/* DNS check result */}
                              {ep.checks?.dns && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: ep.checks.dns.resolved ? 'var(--success)' : 'var(--error)', flexShrink: 0 }} />
                                  <span style={{ color: 'var(--gray)' }}>DNS:</span>
                                  <span style={{ color: ep.checks.dns.resolved ? 'var(--success)' : 'var(--error)', fontWeight: 500 }}>
                                    {ep.checks.dns.resolved ? `Resolved (${ep.checks.dns.addresses?.[0] || 'ok'})` : ep.checks.dns.error || 'Failed'}
                                  </span>
                                </div>
                              )}
                              {/* HTTP check result */}
                              {ep.checks?.http && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: ep.checks.http.ok ? 'var(--success)' : 'var(--error)', flexShrink: 0 }} />
                                  <span style={{ color: 'var(--gray)' }}>HTTP:</span>
                                  <span style={{ color: ep.checks.http.ok ? 'var(--success)' : 'var(--error)', fontWeight: 500 }}>
                                    {ep.checks.http.status} {ep.checks.http.statusText}
                                  </span>
                                </div>
                              )}
                              {/* Alert messages */}
                              {ep.alerts?.map((alert, ai) => (
                                <div key={ai} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.75rem', marginBottom: '0.2rem' }}>
                                  <span style={{ color: alert.severity === 'critical' ? 'var(--error)' : 'var(--gold)', fontWeight: 600, flexShrink: 0 }}>
                                    {alert.code}:
                                  </span>
                                  <span style={{ color: 'var(--charcoal)' }}>{alert.message}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setConfiguring(configuring === 'uptime' ? null : 'uptime')} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Settings size={14} /> Edit Endpoints
                  </button>
                  {configuring === 'uptime' && (
                    <div style={{ ...S.metricTile, marginTop: '1rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.25rem' }}>Edit Monitored Endpoints</div>
                      <p style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '0.75rem' }}>
                        All paths are relative to <strong style={S.mono}>https://{selectedDomain?.domain_name}</strong>
                      </p>
                      {uptimeEndpoints.map((ep, i) => (
                        <div key={i} style={{ marginBottom: '0.75rem', padding: '0.75rem', background: 'var(--white)', borderRadius: '0.5rem', border: '1px solid var(--gray)' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem', alignItems: 'center' }}>
                            <input type="text" placeholder="Label" value={ep.label} onChange={(e) => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], label: e.target.value }; setUptimeEndpoints(u); }} className="form-input" style={{ flex: 1, fontSize: '0.85rem' }} />
                            <div style={{ display: 'flex', alignItems: 'center', flex: 2, gap: 0 }}>
                              <span style={{ ...S.mono, fontSize: '0.75rem', color: 'var(--gray)', padding: '0.45rem 0.4rem', background: 'var(--light-gray)', border: '1px solid var(--gray)', borderRight: 'none', borderRadius: '0.375rem 0 0 0.375rem', whiteSpace: 'nowrap' }}>/{selectedDomain?.domain_name?.split('.')[0]}...</span>
                              <input type="text" placeholder="/api/health" value={ep.path} onChange={(e) => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], path: e.target.value }; setUptimeEndpoints(u); }} className="form-input" style={{ flex: 1, borderRadius: '0 0.375rem 0.375rem 0', fontSize: '0.85rem' }} />
                            </div>
                            {uptimeEndpoints.length > 1 && <button onClick={() => setUptimeEndpoints(uptimeEndpoints.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--error)' }}><Trash2 size={16} /></button>}
                          </div>
                          <details style={{ marginTop: '0.35rem' }}>
                            <summary style={{ fontSize: '0.7rem', color: 'var(--gray)', cursor: 'pointer' }}>Headers {ep.headers?.length > 0 ? `(${ep.headers.length})` : ''}</summary>
                            <div style={{ marginTop: '0.35rem' }}>
                              {(ep.headers || []).map((h, hi) => (
                                <div key={hi} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.25rem', alignItems: 'center' }}>
                                  <input type="text" placeholder="Key" value={h.key} onChange={(e) => { const u = [...uptimeEndpoints]; const hd = [...(u[i].headers || [])]; hd[hi] = { ...hd[hi], key: e.target.value }; u[i] = { ...u[i], headers: hd }; setUptimeEndpoints(u); }} className="form-input" style={{ flex: 1, fontSize: '0.75rem', padding: '0.3rem 0.4rem' }} />
                                  <input type="text" placeholder="Value" value={h.value} onChange={(e) => { const u = [...uptimeEndpoints]; const hd = [...(u[i].headers || [])]; hd[hi] = { ...hd[hi], value: e.target.value }; u[i] = { ...u[i], headers: hd }; setUptimeEndpoints(u); }} className="form-input" style={{ flex: 2, fontSize: '0.75rem', padding: '0.3rem 0.4rem' }} />
                                  <button onClick={() => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], headers: (u[i].headers || []).filter((_, j) => j !== hi) }; setUptimeEndpoints(u); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--error)', fontSize: '0.85rem' }}>×</button>
                                </div>
                              ))}
                              <button onClick={() => { const u = [...uptimeEndpoints]; u[i] = { ...u[i], headers: [...(u[i].headers || []), { key: '', value: '' }] }; setUptimeEndpoints(u); }} style={{ fontSize: '0.7rem', color: 'var(--charcoal)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>+ Add Header</button>
                            </div>
                          </details>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                        <button onClick={() => setUptimeEndpoints([...uptimeEndpoints, { label: '', path: '/', headers: [] }])} className="btn btn-secondary">+ Add</button>
                        <button onClick={handleSaveUptimeConfig} disabled={savingConfig} className="btn btn-primary">{savingConfig ? 'Saving...' : 'Save & Re-check'}</button>
                        <button onClick={() => setConfiguring(null)} className="btn btn-secondary">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Breach ── */}
              {activeTab === 'breach' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ ...S.metricTile, borderColor: (data.totalBreaches || 0) > 0 ? 'var(--error)' : 'var(--gray)' }}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Known Breaches</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: (data.totalBreaches || 0) > 0 ? 'var(--error)' : 'var(--success)' }}>{data.totalBreaches || 0}</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Exposed Credentials</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: (data.exposedCredentials?.count || 0) > 0 ? 'var(--error)' : 'var(--success)' }}>{data.exposedCredentials?.count || 0}</div>
                    </div>
                  </div>
                  {data.breaches?.length > 0 && data.breaches.slice(0, 5).map((breach, i) => (
                    <div key={i} style={{ ...S.metricTile, borderColor: 'var(--error)', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600, color: 'var(--black)' }}>{breach.Name || breach.name}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>{breach.BreachDate || breach.breachDate}</span>
                      </div>
                      {breach.DataClasses && <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '0.25rem' }}>Exposed: {breach.DataClasses.join(', ')}</div>}
                    </div>
                  ))}
                  {(data.totalBreaches || 0) === 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--success)', fontWeight: 600 }}>
                      <CheckCircle size={16} /> No known data breaches found for this domain.
                    </div>
                  )}
                  {data.setupNote && (
                    <div style={{ ...S.metricTile, borderColor: 'var(--gold)', marginTop: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <Settings size={14} style={{ color: 'var(--gold)' }} />
                        <span style={{ fontWeight: 600, color: 'var(--charcoal)', fontSize: '0.85rem' }}>Limited Coverage</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>{data.setupNote}</div>
                    </div>
                  )}
                </div>
              )}

              {/* ── CT ── */}
              {activeTab === 'ct' && (
                <div>
                  {/* Top metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Total Certificates</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--charcoal)' }}>{data.totalCertificates || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>All time in CT logs</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Recent (90 days)</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--charcoal)' }}>{data.recentCertificates?.length || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>Newly issued certs</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Wildcard Certs</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--charcoal)' }}>{data.wildcardCerts || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>*.domain coverage</div>
                    </div>
                    <div style={{ ...S.metricTile, borderColor: (data.expiringSoon || 0) > 0 ? 'var(--gold)' : 'var(--gray)' }}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Expiring Soon</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: (data.expiringSoon || 0) > 0 ? 'var(--gold)' : 'var(--success)' }}>{data.expiringSoon || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>Within 30 days</div>
                    </div>
                    <div style={{ ...S.metricTile, borderColor: (data.alerts?.filter(a => a.code === 'UNKNOWN_ISSUER')?.length || 0) > 0 ? 'var(--error)' : 'var(--gray)' }}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Suspicious</div>
                      <div style={{ fontWeight: 700, fontSize: '1.5rem', color: (data.alerts?.filter(a => a.code === 'UNKNOWN_ISSUER')?.length || 0) > 0 ? 'var(--error)' : 'var(--success)' }}>{data.alerts?.filter(a => a.code === 'UNKNOWN_ISSUER')?.length || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>Unrecognized issuers</div>
                    </div>
                  </div>

                  {/* Issuance timeline (bar chart) */}
                  {data.timeline?.length > 0 && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Certificate Issuance Timeline (12 months)</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '80px' }}>
                        {data.timeline.map((t, i) => {
                          const maxCount = Math.max(...data.timeline.map(x => x.count), 1);
                          const height = t.count > 0 ? Math.max(8, (t.count / maxCount) * 70) : 2;
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <div style={{ fontSize: '0.6rem', color: 'var(--charcoal)', fontWeight: 600 }}>{t.count > 0 ? t.count : ''}</div>
                              <div style={{ width: '100%', height: `${height}px`, background: t.count > 0 ? 'var(--charcoal)' : 'var(--gray)', borderRadius: '2px 2px 0 0', transition: 'height 0.3s' }} />
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                        {data.timeline.map((t, i) => (
                          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.55rem', color: 'var(--gray)', ...S.mono }}>
                            {t.month.split('-')[1]}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Issuers breakdown */}
                  {data.issuers?.length > 0 && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Certificate Authorities ({data.issuers.length})</div>
                      {data.issuers.slice(0, 10).map((iss, i) => {
                        const maxCount = data.issuers[0]?.count || 1;
                        const pct = Math.round((iss.count / (data.totalCertificates || 1)) * 100);
                        return (
                          <div key={i} style={{ marginBottom: '0.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: iss.trusted ? 'var(--success)' : 'var(--error)', flexShrink: 0 }} />
                                <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--black)' }}>{iss.name}</span>
                                {!iss.trusted && <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.35rem', background: 'var(--error)', color: 'var(--white)', borderRadius: '0.2rem', textTransform: 'uppercase' }}>Unknown</span>}
                              </div>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gray)', ...S.mono }}>{iss.count} cert{iss.count !== 1 ? 's' : ''} ({pct}%)</span>
                            </div>
                            <div style={{ height: '4px', background: 'var(--light-gray)', borderRadius: '2px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${(iss.count / maxCount) * 100}%`, background: iss.trusted ? 'var(--charcoal)' : 'var(--error)', borderRadius: '2px', transition: 'width 0.3s' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Subdomains covered */}
                  {data.subdomains?.length > 0 && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Subdomains with Certificates ({data.subdomains.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {data.subdomains.slice(0, 30).map((sub, i) => (
                          <span key={i} style={{ ...S.mono, fontSize: '0.75rem', padding: '0.25rem 0.6rem', background: sub === data.domain ? 'var(--charcoal)' : 'var(--light-gray)', color: sub === data.domain ? 'var(--white)' : 'var(--charcoal)', borderRadius: '0.25rem', border: '1px solid var(--gray)' }}>{sub}</span>
                        ))}
                        {data.subdomains.length > 30 && <span style={{ fontSize: '0.75rem', color: 'var(--gray)', padding: '0.25rem 0.5rem' }}>+{data.subdomains.length - 30} more</span>}
                      </div>
                    </div>
                  )}

                  {/* Recent certificates table */}
                  {data.recentCertificates?.length > 0 && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Recent Certificates (last 90 days)</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--gray)' }}>
                              <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: 'var(--gray)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</th>
                              <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: 'var(--gray)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issuer</th>
                              <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: 'var(--gray)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issued</th>
                              <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: 'var(--gray)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expires</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.recentCertificates.slice(0, 15).map((cert, i) => {
                              const isExpired = cert.notAfter && new Date(cert.notAfter) < new Date();
                              const daysLeft = cert.notAfter ? Math.ceil((new Date(cert.notAfter) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                              const issuerShort = cert.issuer ? (cert.issuer.match(/O=([^,]+)/) || cert.issuer.match(/CN=([^,]+)/) || [null, cert.issuer])[1]?.trim() : 'Unknown';
                              const subjectShort = (cert.subject || '').split('\n')[0]?.trim() || 'N/A';
                              return (
                                <tr key={i} style={{ borderBottom: '1px solid var(--light-gray)' }}>
                                  <td style={{ padding: '0.5rem 0.5rem', ...S.mono, fontSize: '0.75rem', color: 'var(--charcoal)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subjectShort}</td>
                                  <td style={{ padding: '0.5rem 0.5rem', fontSize: '0.78rem', color: 'var(--black)' }}>{issuerShort?.substring(0, 25)}</td>
                                  <td style={{ padding: '0.5rem 0.5rem', ...S.mono, fontSize: '0.72rem', color: 'var(--gray)' }}>{cert.notBefore ? new Date(cert.notBefore).toLocaleDateString() : 'N/A'}</td>
                                  <td style={{ padding: '0.5rem 0.5rem', ...S.mono, fontSize: '0.72rem', color: isExpired ? 'var(--error)' : daysLeft <= 30 ? 'var(--gold)' : 'var(--gray)' }}>
                                    {cert.notAfter ? new Date(cert.notAfter).toLocaleDateString() : 'N/A'}
                                    {daysLeft != null && !isExpired && daysLeft <= 30 && <span style={{ marginLeft: '0.25rem', fontSize: '0.65rem', fontWeight: 600 }}>({daysLeft}d)</span>}
                                    {isExpired && <span style={{ marginLeft: '0.25rem', fontSize: '0.65rem', fontWeight: 600 }}>(expired)</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {data.recentCertificates.length > 15 && (
                          <div style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.75rem', color: 'var(--gray)' }}>
                            Showing 15 of {data.recentCertificates.length} recent certificates
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Explainer */}
                  <div style={{ fontSize: '0.82rem', color: 'var(--gray)', ...S.metricTile, lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--charcoal)' }}>What is Certificate Transparency?</strong><br />
                    Every publicly-trusted CA must log certificates to public CT logs before issuance. This allows domain owners to detect unauthorized certificates that could be used for man-in-the-middle attacks. We monitor these logs for your domain and flag any certificates from unrecognized issuers.
                  </div>
                </div>
              )}

              {/* ── CDN ── */}
              {activeTab === 'cdn' && (
                <div>
                  {/* Top metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>CDN Provider</div>
                      <div style={{ fontWeight: 700, color: 'var(--charcoal)' }}>{data.cdn?.detected ? data.cdn.provider || 'Detected' : 'None detected'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>{data.cdn?.behindCDN ? 'Traffic proxied through CDN' : 'Direct to origin'}</div>
                    </div>
                    <div style={{ ...S.metricTile, borderColor: data.origin?.exposed ? 'var(--error)' : 'var(--success)' }}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Origin Exposure</div>
                      <div style={{ fontWeight: 700, color: data.origin?.exposed ? 'var(--error)' : 'var(--success)' }}>{data.origin?.exposed ? 'EXPOSED' : 'Protected'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray)' }}>{data.origin?.exposed ? `${data.origin.ipAddresses?.length || 0} leak(s) found` : 'Origin hidden behind proxy'}</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Compression</div>
                      <div style={{ fontWeight: 700, color: data.performance?.compression ? 'var(--success)' : 'var(--gold)' }}>{data.performance?.compression ? 'Enabled' : 'Disabled'}</div>
                    </div>
                    <div style={S.metricTile}>
                      <div style={{ ...S.label, fontSize: '0.65rem', marginBottom: '0.25rem' }}>HTTP/2 · HTTP/3</div>
                      <div style={{ fontWeight: 700, color: 'var(--charcoal)', fontSize: '0.9rem' }}>
                        {data.performance?.http2 ? 'H2' : '—'} · {data.performance?.http3 ? 'H3' : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Origin Exposure Detail */}
                  {data.origin?.exposed && data.origin.ipAddresses?.length > 0 && (
                    <div style={{ ...S.metricTile, borderColor: 'var(--error)', marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.5rem', color: 'var(--error)' }}>Exposed Origin IPs</div>
                      <p style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '0.75rem' }}>
                        The following DNS records resolve to non-CDN IPs, revealing your origin server. Attackers can use these to bypass CDN protections (DDoS mitigation, WAF, rate limiting).
                      </p>
                      {data.origin.ipAddresses.map((entry, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', background: 'var(--light-gray)', borderRadius: '0.375rem', marginBottom: '0.35rem', border: '1px solid var(--gray)' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--error)', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <span style={{ ...S.mono, fontSize: '0.8rem', fontWeight: 600, color: 'var(--charcoal)' }}>{entry.hostname}</span>
                            {entry.type === 'dns_only' && <span style={{ fontSize: '0.65rem', marginLeft: '0.5rem', padding: '0.1rem 0.35rem', background: 'var(--gold)', color: 'var(--black)', borderRadius: '0.2rem', fontWeight: 600 }}>DNS-only mode</span>}
                          </div>
                          <span style={{ ...S.mono, fontSize: '0.8rem', color: 'var(--error)', fontWeight: 600 }}>{entry.ip || entry.ips?.join(', ')}</span>
                        </div>
                      ))}
                      {data.origin.recommendations?.length > 0 && (
                        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--gray)' }}>
                          <div style={{ ...S.label, fontSize: '0.6rem', marginBottom: '0.35rem' }}>Recommendations</div>
                          {data.origin.recommendations.map((rec, i) => (
                            <div key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.78rem', color: 'var(--charcoal)', marginBottom: '0.2rem' }}>
                              <span style={{ color: 'var(--gold)' }}>—</span> {rec}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Origin check results (what subdomains were checked) */}
                  {data.origin?.checkedSubdomains?.length > 0 && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.5rem' }}>Subdomain Scan Results</div>
                      <p style={{ fontSize: '0.78rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>
                        We checked {data.origin.checkedSubdomains.length} common subdomains for origin IP leakage.
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {data.origin.checkedSubdomains.map((sub, i) => {
                          const colors = { exposed: { bg: '#fef2f2', border: 'var(--error)', color: 'var(--error)' }, behind_cdn: { bg: '#f0fdf4', border: 'var(--success)', color: 'var(--success)' }, not_found: { bg: 'var(--light-gray)', border: 'var(--gray)', color: 'var(--gray)' }, error: { bg: 'var(--light-gray)', border: 'var(--gray)', color: 'var(--gray)' }, cname_only: { bg: 'var(--light-gray)', border: 'var(--gray)', color: 'var(--gray)' } };
                          const c = colors[sub.status] || colors.not_found;
                          return (
                            <span key={i} style={{ ...S.mono, fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: '0.25rem', fontWeight: sub.status === 'exposed' ? 600 : 400 }} title={sub.status === 'exposed' ? `IP: ${sub.ips?.join(', ')}` : sub.status}>
                              {sub.hostname.replace(`.${data.domain}`, '')}
                              {sub.status === 'exposed' && ' ⚠'}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.65rem', color: 'var(--gray)' }}>
                        <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: 'var(--error)', borderRadius: '2px', marginRight: '3px' }} /> Exposed</span>
                        <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: 'var(--success)', borderRadius: '2px', marginRight: '3px' }} /> Behind CDN</span>
                        <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: 'var(--gray)', borderRadius: '2px', marginRight: '3px' }} /> Not found</span>
                      </div>
                    </div>
                  )}

                  {/* Security Headers */}
                  {data.headers?.security && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Security Headers</div>
                      {Object.entries({
                        'Strict-Transport-Security': data.headers.security.strictTransportSecurity || data.ssl?.hsts?.enabled,
                        'X-Frame-Options': data.headers.security.xFrameOptions,
                        'X-Content-Type-Options': data.headers.security.xContentTypeOptions,
                        'Content-Security-Policy': data.headers.security.contentSecurityPolicy,
                        'Referrer-Policy': data.headers.security.referrerPolicy,
                        'Permissions-Policy': data.headers.security.permissionsPolicy
                      }).map(([name, value], i) => {
                        const present = !!value && value !== false;
                        const displayValue = typeof value === 'string' ? (value.length > 60 ? value.substring(0, 60) + '...' : value) : (present ? 'Set' : 'Missing');
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', borderBottom: i < 5 ? '1px solid var(--light-gray)' : 'none' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: present ? 'var(--success)' : 'var(--gold)', flexShrink: 0 }} />
                            <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 500, color: 'var(--black)' }}>{name}</div>
                            <div style={{ ...S.mono, fontSize: '0.72rem', color: present ? 'var(--success)' : 'var(--gold)', fontWeight: present ? 400 : 600, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={typeof value === 'string' ? value : ''}>
                              {displayValue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Caching Headers */}
                  {data.headers?.caching && (
                    <div style={{ ...S.metricTile, marginBottom: '1.5rem' }}>
                      <div style={{ ...S.label, marginBottom: '0.75rem' }}>Caching Configuration</div>
                      {Object.entries({
                        'Cache-Control': data.headers.caching.cacheControl,
                        'ETag': data.headers.caching.etag,
                        'Last-Modified': data.headers.caching.lastModified,
                        'CF-Cache-Status': data.headers.cdnSpecific?.cfCacheStatus
                      }).map(([name, value], i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', borderBottom: i < 3 ? '1px solid var(--light-gray)' : 'none' }}>
                          <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 500, color: 'var(--black)' }}>{name}</div>
                          <div style={{ ...S.mono, fontSize: '0.72rem', color: value ? 'var(--charcoal)' : 'var(--gray)', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value || ''}>
                            {value || 'Not set'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Alerts ── */}
              {data.alerts?.length > 0 && (
                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--gray)' }}>
                  <div style={{ ...S.label, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertCircle size={14} /> Alerts & Recommendations
                  </div>
                  {data.alerts.map((alert, i) => (
                    <div key={i} style={{ ...S.metricTile, marginBottom: '0.5rem', borderColor: alert.severity === 'critical' ? 'var(--error)' : alert.severity === 'warning' ? 'var(--gold)' : 'var(--gray)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', padding: '0.15rem 0.4rem', borderRadius: '0.2rem', background: alert.severity === 'critical' ? 'var(--error)' : alert.severity === 'warning' ? 'var(--gold)' : 'var(--success)', color: alert.severity === 'warning' ? 'var(--black)' : 'var(--white)' }}>{alert.severity}</span>
                        <span style={{ fontWeight: 600, color: 'var(--black)', fontSize: '0.875rem' }}>{alert.code}: {alert.message}</span>
                      </div>
                      {alert.remediation && <div style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>Fix: {alert.remediation}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MonitoringDashboard;
